import React, {useState, useEffect, useCallback} from "react"
import {useTranslation} from 'react-i18next'
import Cookies from 'universal-cookie'
import * as Requests from "../../library/requests/Requests"
import Spinner from "../common/Spinner"
import {SwitchTransition, CSSTransition} from "react-transition-group"
import "../../css/Transitions.css"
import "../../css/GalleryOptIn.css"
import GallerySample from "./GallerySample"
import Config from "../../library/Config"
import {UploadResponse} from "../../library/Upload"
import Participant from "../../library/Participant"

interface WrapUpViewProps {
  participant: Participant
  config: Config
  compositeImage: string
  rawFrame: string
  reset: () => void
  finish: (uploadCode: string) => void
}

function WrapUpView({
  participant,
  config,
  compositeImage,
  rawFrame,
  reset,
  finish
}: WrapUpViewProps) {
  const {t} = useTranslation()

  const [uploadCode, setUploadCode] = useState<string | undefined>()
  const [uploadShareUrl, setUploadShareUrl] = useState<string | undefined>()
  const [uploadError, setUploadError] = useState<string | undefined>()
  const [showGalleryOptIn, setShowGalleryOptIn] = useState(config.showGalleryOptIn)
  const [isUploading, setIsUploading] = useState(!config.showGalleryOptIn)
  const [isProcessing, setIsProcessing] = useState(false)

  const checkProcessingStatus = useCallback((id: number, code: string) => {
    Requests.checkProcessedStatus(id, (isProcessed: boolean) => {
      if (isProcessed) {
        setIsProcessing(false)
        finish(code)
      } else {
        setTimeout(() => checkProcessingStatus(id, code), 1000)
      }
    })
  }, [finish])

  const startUpload = useCallback((isPublic: boolean) => {
    setIsUploading(true)
    setShowGalleryOptIn(false)

    Requests.upload(
      config,
      participant.sessionId,
      compositeImage,
      rawFrame,
      isPublic,
      participant ?? null,
      (json: UploadResponse) => {
        if (json.success) {
          new Cookies().set("lastUploadId", json.id)
          new Cookies().set("lastUploadShareUrl", json.shareURL)

          setIsUploading(false)
          setIsProcessing(true)
          setUploadCode(json.url)
          setUploadShareUrl(json.shareURL)

          checkProcessingStatus(json.id, json.url)
        } else {
          setIsUploading(false)
          setUploadError(json.message ?? "An error occurred while uploading.")
        }
      })
  }, [config, participant, compositeImage, rawFrame, checkProcessingStatus])

  useEffect(() => {
    if (!compositeImage) {
      if (uploadCode && uploadShareUrl) {
        window.open(uploadShareUrl)
        reset()
      }
      return
    }

    if (!showGalleryOptIn) {
      const isUploadPublic = config.gallery.privacy !== "private"
      startUpload(isUploadPublic)
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getUploadStatusKey = () => {
    if (isUploading) {
      return "finish.uploading"
    }
    if (isProcessing) {
      return "finish.processing"
    }
    return "finish.finishing"
  }

  const theme = config.theme
  const statusKey = getUploadStatusKey()

  if (showGalleryOptIn) {
    return (
      <CSSTransition in appear timeout={1000} classNames="slideDown">
        <div id="galleryOptIn">
          <h2 className="prompt" style={theme.textStyle}>{t("finish.galleryOptIn.prompt", {galleryTitle: config.gallery.title})}</h2>
          <p className="subtext" style={theme.textStyle}>{t("finish.galleryOptIn.explanation")}</p>
          <button className="bigButton" style={theme.secondaryBigButtonStyle} onClick={() => startUpload(false)}>
            {t("finish.galleryOptIn.no")}
          </button>
          <button className="bigButton" style={theme.bigButtonStyle} onClick={() => startUpload(true)}>
            {t("finish.galleryOptIn.yes")}
          </button>
          <GallerySample gallery={config.gallery}/>
        </div>
      </CSSTransition>
    )
  }

  return (
    <>
      {uploadError == null &&
        <div className="mt-5">
          <Spinner color={theme.textStyle.color}/>
        </div>
      }
      <SwitchTransition>
        <CSSTransition key={statusKey} timeout={500} classNames="uploadStatus">
          <h2 className="mt-5" style={theme.textStyle}>
            {uploadError ?
              uploadError :
              t(statusKey)
            }
          </h2>
        </CSSTransition>
      </SwitchTransition>
    </>
  )
}

export default WrapUpView
